<template>
  <main :id="settings.locationId" role="main">
    <div class="grid-x grid-margin-x">
      <facet-group-container
        v-if="filters"
        :settings="settings"
        :filters="filters"
        :selected-filters="selectedFilters"
        :base-properties="baseProperties"
      />
      <div v-if="hasProducts" class="off-canvas-content cell small-12 large-10" data-off-canvas-content>
        <trek-button secondary block icon="list" class="hide-for-large mt-2" data-toggle="b2b-off-canvas-facet">
          <span v-html="bp('filters')"></span>
        </trek-button>
        <plp-view-options
          v-show="options && options.totalCount"
          :options="options"
          class="my-2 lg:my-4"
          :is-expand-all-visible="isExpandAllButtonVisible"
          :can-change-page-size="isGrid"
          :view-options="viewOptions"
          :page-size-options="settings.pageSizeOptions"
          :sort-options="options.sortOptions"
          :base-properties="baseProperties"
          @view-mode-changed="onViewModeChanged"
        />
        <plp-list-view-container
          v-if="isListView"
          :base-properties="baseProperties"
          :products="products"
          :apple-table-headers="appleTableHeaders"
          :compare-button-enabled="settings.compareButtonEnabled"
          :settings="settings"
          :options="options"
          @item-state-changed="updateCollapseState"
        />
        <plp-grid-view-container
          v-if="isGrid"
          :base-properties="baseProperties"
          :products="products"
          :compare-button-enabled="settings.compareButtonEnabled"
          :settings="settings"
          :options="options"
        />
        <div
          v-if="hasProducts"
          class="md:flex md:justify-between border-gray-10 pt-4 pb-4"
          :class="{'border-t': isGrid}"
        >
          <div>
            <pagination
              v-if="totalPages > 1"
              :page-number="viewOptions.currentPage"
              :page-count="totalPages"
              @prev-page="prevPage"
              @next-page="nextPage"
              @page-change="gotoPage"
            />
          </div>
          <div>
            <plp-view-options
              v-show="options && options.totalCount"
              :options="options"
              :is-total-count-visible="false"
              :is-expand-all-visible="isExpandAllButtonVisible"
              :can-change-page-size="isGrid"
              :view-options="viewOptions"
              :page-size-options="settings.pageSizeOptions"
              :sort-options="options.sortOptions"
              :base-properties="baseProperties"
              @view-mode-changed="onViewModeChanged"
            />
          </div>
        </div>
      </div>
      <plp-no-results
        v-if="!hasProducts && !loading"
        :base-properties="baseProperties"
        :class="filters ? 'cell small-12 large-10' : 'w-full'"
      />
    </div>
    <trek-loading v-if="loading" bottom :is-loading="loading"></trek-loading>
  </main>
</template>

<script>
import {mapState, mapActions} from 'vuex';
import TrekLoading from '@/components/TrekLoading';
import PlpViewOptions from './PlpViewOptions';
import PlpListViewContainer from './PlpListViewContainer';
import PlpGridViewContainer from './PlpGridViewContainer';
import PlpNoResults from './PlpNoResults';
import Pagination from '@/components/Pagination';
import {Url} from '@/utils/url';
import FacetGroupContainer from './FacetGroupContainer';
import {Scroll} from '@/utils/scroll';
import TrekButton from '@/components/TrekButton';

export default {
  components: {
    FacetGroupContainer,
    Pagination,
    PlpNoResults,
    TrekLoading,
    PlpViewOptions,
    PlpListViewContainer,
    PlpGridViewContainer,
    TrekButton,
  },
  props: {
    appleTableHeaders: {
      type: Array,
      default: () => [],
    },
    settings: {
      type: Object,
      default: undefined,
    },
  },

  computed: {
    ...mapState('plp', ['products', 'filters', 'selectedFilters', 'options', 'loading', 'viewOptions']),

    isExpandAllButtonVisible() {
      return this.settings.locationId !== 'archive';
    },

    isGrid() {
      return this.viewOptions.viewMode === 'grid';
    },

    isListView() {
      return this.viewOptions.viewMode === 'listview';
    },

    totalPages() {
      return parseInt(this.options.totalNumberOfPages);
    },

    hasProducts() {
      return this.products && this.products.length > 0;
    },
  },

  mounted() {
    this.init();
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.initFromQueryParams);
    window.removeEventListener('plp-set-api-url', this.setApiUrl);
  },

  methods: {
    ...mapActions('plp', [
      'fetchResults',
      'updateViewOption',
      'setCategoryId',
      'setSearchQueryText',
      'resetViewOptions',
      'setFiltersQuery',
      'setApiBaseUrl',
      'updateCollapseState',
    ]),

    setApiUrl(ev) {
      if (!ev.detail && !ev.detail.url) {
        return;
      }
      this.setApiBaseUrl(ev.detail.url);
      this.fetchResults();
    },

    init() {
      window.removeEventListener('popstate', this.initFromQueryParams);
      window.removeEventListener('plp-set-api-url', this.setApiUrl);

      window.addEventListener('popstate', this.initFromQueryParams);
      window.addEventListener('plp-set-api-url', this.setApiUrl);

      this.initFromQueryParams();
      this.fetchResults().then(() => {
        const ev = new CustomEvent('plp-data-loaded', {
          detail: {
            options: this.options,
            settings: this.settings,
          },
        });
        window.dispatchEvent(ev);
      });
    },

    getCategoryId() {
      const matches = window.location.href.match(/\/c\/((\w|-)+)(\/|\?|^)?/);
      if (matches && matches.length > 2 && matches[1].length > 0) {
        return matches[1];
      }
      return '';
    },

    initPlp() {
      // Store current URL to local storage, it's used by compare rack to return back when user clicks "Add Product" link.
      const lastPlpUrl = JSON.stringify(window.location.pathname);
      window.localStorage.setItem('latest_plp_url', lastPlpUrl);
    },

    initFromQueryParams() {
      const categoryId = this.getCategoryId();
      if (categoryId) {
        this.setCategoryId(categoryId);
        this.initPlp();
      }

      this.resetViewOptions();
      const params = Url.parse(window.location.href);
      let viewOptions = {};
      for (let param in params) {
        switch (param) {
          case 'pageSize':
            viewOptions.pageSize = params[param];
            break;
          case 'sort':
            viewOptions.sortCode = params[param];
            break;
          case 'viewMode':
            viewOptions.viewMode = params[param];
            break;
          case 'currentPage':
            viewOptions.currentPage = params[param];
            break;
          case 'facets':
            // Make sure each GET parameter is encoded to prevent issues with special characters such as ? or &.
            var query = params[param]
              .split(':')
              .map((item) => encodeURIComponent(item))
              .join(':');
            this.setFiltersQuery(query);
            break;
          case 'text':
            this.setSearchQueryText(params[param]);
            break;
        }
      }

      if (Object.keys(viewOptions).length > 0) {
        this.updateViewOption(viewOptions);
      }
    },

    prevPage() {
      this.gotoPage(this.viewOptions.currentPage - 1);
    },

    nextPage() {
      this.gotoPage(this.viewOptions.currentPage + 1);
    },

    gotoPage(pageNumber) {
      if (this.loading || pageNumber < 0 || pageNumber >= this.options.totalNumberOfPages) {
        return;
      }

      this.cleanExpandedGrids();
      this.updateViewOption({currentPage: pageNumber});
      Scroll.toPageTop();
    },

    cleanExpandedGrids() {
      // UX Optimization. This method cleans DOM tree from expanded grids and allows
      // browser work faster when user switches between pages.
      document.querySelectorAll('[data-plp-grid]').forEach((grid) => grid.remove());
    },

    onViewModeChanged(newMode) {
      this.updateViewOption({viewMode: newMode});
    },
  },
};
</script>
